import { Component, Input, Output, EventEmitter, OnDestroy, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { interval } from 'rxjs';

@Component({
    selector: 'activity-timer',
    templateUrl: './activity-timer.component.html',
    styleUrls: ['./activity-timer.component.scss'],
    standalone: true,
    imports: [CommonModule, MatProgressBarModule]
})
export class ActivityTimerComponent implements AfterViewInit, OnDestroy
{
    /** Timeout in seconds (default 90) */
    @Input() timeoutSeconds = 90;

    /** A flag indicating if the time should ignore the watched value and start immediately.
     * When false, the timer will only run when the watched input has a value.
     */
    @Input() startImmediately: boolean = false;

    /** A flag indicating if the time should IGNORE changes to the input, and continue counting down regardless. */
    @Input() ignoreChanges: boolean = false;

    /** The string value to watch; when non-empty the timer runs */
    private _watched = '';
    @Input()
    set watchedValue(v: string) {
        this._watched = v ?? '';
        this._onWatchedChanged();
    }

    get watchedValue(): string {
        return this._watched;
    }

    /** Emitted when the timer reaches the timeout */
    @Output() timedOut = new EventEmitter<void>();

    // Progress percentage (0..100). Shows 100 when idle.
    progress = 100;

    private _sub: import('rxjs').Subscription | null = null;
    private _endTime = 0;

    private _onWatchedChanged() {
        if (this.ignoreChanges) {
            return;
        }

        if (this._watched && this._watched.toString().trim() !== '') {
            // Start or re-start the timer when ever the input value changes and is non-blank.
            this.start();
        } else {
            // if the input is blank, stop the timer.
            this.stop();
        }
    }

    private start() {
        this.stop();
        const durationMs = Math.max(1, this.timeoutSeconds) * 1000;
        const now = Date.now();
        this._endTime = now + durationMs;

        // initial update
        this._updateProgress(durationMs);

        // update frequently for a smooth progress bar
        this._sub = interval(100).subscribe(() => {
            this._updateProgress(durationMs);
        });
    }

    private _updateProgress(durationMs: number) {
        const remaining = Math.max(0, this._endTime - Date.now());
        this.progress = Math.round((remaining / durationMs) * 100);
        if (this.progress < 0) { this.progress = 0; }

        if (remaining <= 0) {
            this.timedOut.emit();
            this.stop();
        }
    }

    private stop() {
        if (this._sub) {
            this._sub.unsubscribe();
            this._sub = null;
        }
        this.progress = 100;
    }

    ngAfterViewInit(): void {
        if (this.startImmediately) {
            // Start immediately, regardless of watched input value.
            this.start();
        } else if (this.ignoreChanges) {
            // Stop/reset the timer.
            this.stop();
        } else {
            // start or stop based on the current watched value.
            this._onWatchedChanged();
        }
    }

    ngOnDestroy(): void {
        this.stop();
    }
}
